import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";
import {
    LocalizationProvider,
    DateTimePicker,
    DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const ACCENT = "#2563eb";

// Date / date-time picker styled like the other form fields:
// uppercase label above, 42px rounded box, blue focus glow, red border on error.
export default function DateTimePickerField({
    label = "Select Date & Time",
    value,
    onChange,
    format = "DD-MM-YYYY HH:mm:ss",
    error = false,
    required = false,
}) {
    const isDateOnly = format === "DD-MM-YYYY";
    const CommonProps = {
        value,
        onChange,
        format,
        minDate: dayjs().startOf("day"),
        slotProps: {
            textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(error),
            },
            openPickerIcon: { sx: { color: ACCENT, fontSize: 22 } },
            // Calendar popup
            desktopPaper: {
                sx: {
                    mt: 0.75,
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 16px 32px -16px rgba(15,23,42,0.3)",
                    "& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover": { bgcolor: ACCENT },
                    "& .MuiPickersDay-today:not(.Mui-selected)": { borderColor: ACCENT },
                    "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": { bgcolor: ACCENT },
                    "& .MuiDialogActions-root .MuiButton-root": { textTransform: "none", fontWeight: 700, color: ACCENT },
                },
            },
        },
        sx: {
            width: "100%",
            "& .MuiPickersInputBase-root, & .MuiInputBase-root": {
                height: 42,
                borderRadius: 2.5,
                bgcolor: "#fff",
                fontSize: 14,
                color: "#111827",
                transition: "box-shadow .15s ease",
            },
            "& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline": {
                borderWidth: "1.5px",
                borderColor: error ? "#ef4444" : "#e5e7eb",
            },
            "& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "#ef4444" : "#cbd5e1",
            },
            "& .MuiPickersInputBase-root.Mui-focused, & .MuiInputBase-root.Mui-focused": {
                boxShadow: `0 0 0 4px ${error ? "rgba(239,68,68,0.12)" : "rgba(37,99,235,0.14)"}`,
            },
            "& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: "1.5px",
                borderColor: error ? "#ef4444" : ACCENT,
            },
            "& .MuiInputBase-input": { padding: "8px 12px", fontSize: 14 },
            // Calendar icon: readable size + soft hover circle
            "& .MuiInputAdornment-root .MuiIconButton-root": { p: 0.75, mr: -0.5, color: ACCENT, "&:hover": { bgcolor: "rgba(37,99,235,0.08)" } },
            "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: "22px !important", color: ACCENT },
        },
    };

    return (
        <Box>
            {label && (
                <Typography
                    component="label"
                    sx={{ display: "block", mb: 0.9, fontSize: 13, fontWeight: 500, letterSpacing: 0.6, textTransform: "uppercase", color: "#4b5563" }}
                >
                    {label}
                    {required && (
                        <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                            *
                        </Box>
                    )}
                </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                {isDateOnly ? (
                    <DatePicker {...CommonProps} />
                ) : (
                    <DateTimePicker {...CommonProps} />
                )}
            </LocalizationProvider>
        </Box>
    );
}
